import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import axios from "axios";

declare global {
  interface Window {
    Intercom: (action: string, ...args: unknown[]) => void;
    intercomSettings: Record<string, unknown>;
  }
}

interface IntercomContextType {
  isLoaded: boolean;
  loadIntercom: (jwt: string) => void;
  updateIntercom: (data?: Record<string, unknown>) => void;
  trackEvent: (eventName: string, metadata?: Record<string, unknown>) => void;
  showIntercom: () => void;
  hideIntercom: () => void;
}

const IntercomContext = createContext<IntercomContextType | undefined>(
  undefined
);

interface IntercomProviderProps {
  children: ReactNode;
  appId?: string;
  hideDefaultLauncher?: boolean;
  shouldLoad?: boolean;
}

export const IntercomProvider = ({
  children,
  appId,
  hideDefaultLauncher = false,
  shouldLoad = true,
}: IntercomProviderProps) => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!appId || !shouldLoad) return undefined;

    const script = document.createElement("script");
    script.innerHTML = `
      window.intercomSettings = {
        app_id: "${appId}"
      };
    `;
    document.head.appendChild(script);

    const intercomScript = document.createElement("script");
    intercomScript.src = `https://widget.intercom.io/widget/${appId}`;
    intercomScript.async = true;
    intercomScript.onload = () => setIsLoaded(true);
    document.head.appendChild(intercomScript);

    return () => {
      document.head.removeChild(script);
      document.head.removeChild(intercomScript);
    };
  }, [appId, shouldLoad]);

  const loadIntercom = (jwt: string) => {
    if (!window.Intercom || !appId || !shouldLoad) return;

    const intercomConfig = {
      api_base: "https://api-iam.intercom.io",
      app_id: appId,
      intercom_user_jwt: jwt,
      hide_default_launcher: hideDefaultLauncher,
    };

    window.Intercom("boot", intercomConfig);
  };

  const updateIntercom = (data?: Record<string, unknown>) => {
    if (!window.Intercom) return;

    window.Intercom("update", data);
  };

  const trackEvent = (
    eventName: string,
    metadata?: Record<string, unknown>
  ) => {
    if (!window.Intercom) return;

    window.Intercom("trackEvent", eventName, metadata);
  };

  const showIntercom = () => {
    if (!window.Intercom) return;

    window.Intercom("update");
    window.Intercom("show");
  };

  const hideIntercom = () => {
    if (!window.Intercom) return;

    window.Intercom("hide");
  };

  const value: IntercomContextType = {
    isLoaded,
    loadIntercom,
    updateIntercom,
    trackEvent,
    showIntercom,
    hideIntercom,
  };

  return (
    <IntercomContext.Provider value={value}>
      {children}
    </IntercomContext.Provider>
  );
};

export const useIntercom = () => {
  const context = useContext(IntercomContext);
  if (context === undefined) {
    throw new Error("useIntercom must be used within an IntercomProvider");
  }
  return context;
};

export const useIntercomAuth = (
  authToken: string | null,
  apiOrigin: string
) => {
  const { loadIntercom, isLoaded } = useIntercom();

  useEffect(() => {
    if (!authToken || !isLoaded) {
      return;
    }

    const fetchIntercomData = async () => {
      try {
        const response = await axios.get(`${apiOrigin}intercom_token/`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });

        const { intercom_user_jwt } = response.data;
        if (intercom_user_jwt) {
          loadIntercom(intercom_user_jwt);
        }
      } catch (error) {
        console.error("Failed to load Intercom data:", error);
      }
    };

    fetchIntercomData();
  }, [authToken, isLoaded, loadIntercom, apiOrigin]);
};
